import React, { useEffect, useState } from 'react'
import { IconButton, Stack, SvgIcon, Tooltip, Typography, InputBase, Box } from '@mui/material'
import { Toggle } from '../../../shared/components/Toggle/Toggle'
import { ReactComponent as TargetIcon } from '../../../assets/images/target.svg'
import { ReactComponent as FoldersIcon } from '../../../assets/images/folders.svg'
import { PulsingCircle } from '../../../shared/components/PulsingCircle'
import { TppAdminSettingsButton } from './TppAdminSettings'
import { TppSection, TppRow } from '../types'
import { useTppMutations } from '../hooks/useTppMutations'
import { useIsMobileDevice } from '../../../shared/hooks/useIsMobileDevice'

type SavedVersionInfo = {
    columnTitle: string
    savedAt: Date
} | null

type TppHeaderProps = {
    tppId: string
    title: string
    assetAmbition: string
    viewMode: 'all' | 'summary'
    onViewModeChange: (mode: 'all' | 'summary') => void
    onOpenChangeLog?: () => void
    savedVersionInfo?: SavedVersionInfo
    onSavedVersionDismiss?: () => void
    sections?: TppSection[]
    rows?: TppRow[]
    canEdit?: boolean
}

const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

const TppHeader: React.FC<TppHeaderProps> = ({
    tppId,
    title,
    assetAmbition,
    viewMode,
    onViewModeChange,
    onOpenChangeLog,
    savedVersionInfo,
    onSavedVersionDismiss,
    sections = [],
    rows = [],
    canEdit = true,
}) => {
    const { updateAssetAmbition } = useTppMutations(tppId)

    // Tooltip state for version save notification
    const [showTooltip, setShowTooltip] = useState(false)

    useEffect(() => {
        if (savedVersionInfo) {
            setShowTooltip(true)
            const timer = setTimeout(() => {
                setShowTooltip(false)
                onSavedVersionDismiss?.()
            }, 7000)
            return () => clearTimeout(timer)
        }
    }, [savedVersionInfo, onSavedVersionDismiss])

    // Inline editing state for asset ambition
    const [isEditingAmbition, setIsEditingAmbition] = useState(false)
    const [ambitionDraft, setAmbitionDraft] = useState(assetAmbition || '')
    const ambitionInputRef = React.useRef<HTMLTextAreaElement | HTMLInputElement | null>(null)

    const isMobileDevice = useIsMobileDevice()

    useEffect(() => {
        // keep draft in sync when upstream changes (e.g., refetch)
        if (!isEditingAmbition) setAmbitionDraft(assetAmbition || '')
    }, [assetAmbition, isEditingAmbition])

    useEffect(() => {
        if (!isEditingAmbition) return

        // Ensure focus lands at the end of the text (not the start)
        const el = ambitionInputRef.current
        if (!el) return

        // Wait a tick so MUI mounts/focuses the textarea
        window.setTimeout(() => {
            try {
                el.focus()
                const len = (el as any).value?.length ?? 0
                ;(el as any).setSelectionRange?.(len, len)
            } catch {
                // ignore
            }
        }, 0)
    }, [isEditingAmbition])

    const handleSaveAmbition = () => {
        const next = ambitionDraft.trim()
        // no-op if unchanged
        if (next === (assetAmbition || '').trim()) {
            setIsEditingAmbition(false)
            return
        }

        updateAssetAmbition.mutate(
            { assetAmbition: next },
            {
                onSettled: () => {
                    setIsEditingAmbition(false)
                },
            }
        )
    }

    const handleCancelAmbition = () => {
        setAmbitionDraft(assetAmbition || '')
        setIsEditingAmbition(false)
    }

    return (
        <Stack
            direction={isMobileDevice ? 'column' : 'row'}
            p={isMobileDevice ? 2 : 5}
            pr={2}
            gap={4}
            justifyContent="space-between"
        >
            <Stack direction="column" gap={3}>
                <Stack direction="row" color="figma.grayscale.textHier1" gap={1.5} alignItems="center">
                    <SvgIcon component={TargetIcon} inheritViewBox sx={{ fontSize: '32px' }} />
                    <Typography variant="28x600">{title}</Typography>
                </Stack>
                <Stack direction="row" color="figma.grayscale.textHier1" gap={2} alignItems="center">
                    <Tooltip
                        open={showTooltip}
                        arrow
                        placement="right"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: 'figma.grayscale.tier7',
                                    color: 'figma.grayscale.textHier1',
                                    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20)',
                                    borderRadius: '12px',
                                    p: 1.5,
                                    maxWidth: 'none',
                                },
                            },
                            arrow: {
                                sx: {
                                    color: 'figma.grayscale.tier7',
                                },
                            },
                        }}
                        title={
                            savedVersionInfo ? (
                                <Stack gap={0.5}>
                                    <Typography variant="16x600" color="figma.grayscale.textHier1">
                                        {savedVersionInfo.columnTitle}
                                    </Typography>
                                    <Typography variant="14x400" color="figma.grayscale.textHier2">
                                        Version Successfully Saved: {formatDate(savedVersionInfo.savedAt)}
                                    </Typography>
                                </Stack>
                            ) : (
                                ''
                            )
                        }
                    >
                        <Stack direction="row" position="relative">
                            <IconButton
                                onClick={onOpenChangeLog}
                                size="large"
                                title={showTooltip ? undefined : 'Change Log & Version History'}
                                sx={{
                                    color: 'figma.primary',
                                    p: 1,
                                    ml: -1,
                                }}
                            >
                                <SvgIcon component={FoldersIcon} inheritViewBox sx={{ fontSize: '32px' }} />
                            </IconButton>
                            <PulsingCircle visible={showTooltip} absolute />
                        </Stack>
                    </Tooltip>

                    <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.75}>
                        <Typography
                            variant="16x400"
                            onClick={() => onViewModeChange('all')}
                            whiteSpace="nowrap"
                            sx={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            Show all
                        </Typography>
                        <Toggle
                            checked={viewMode === 'summary'}
                            onChange={() => onViewModeChange(viewMode === 'all' ? 'summary' : 'all')}
                            sx={{ m: 0 }}
                        />
                        <Typography
                            variant="16x400"
                            onClick={() => onViewModeChange('summary')}
                            whiteSpace="nowrap"
                            sx={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            Executive Summary
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>
            <Stack direction="row" alignItems="flex-start" gap={2}>
                <Stack
                    width={isMobileDevice ? 'auto' : '500px'}
                    flex={isMobileDevice ? 1 : 'auto'}
                    flexShrink={0}
                    direction="column"
                    gap={1}
                >
                    <Typography variant="18x700" color="figma.grayscale.textHier1">
                        Asset Ambition / Vision
                    </Typography>
                    <Stack
                        direction="column"
                        p={2}
                        bgcolor="figma.grayscale.tier7"
                        borderRadius="12px"
                        sx={{
                            cursor: isEditingAmbition ? 'text' : canEdit ? 'pointer' : 'default',
                            border: '2px solid',
                            borderColor: 'transparent',
                            boxSizing: 'border-box',
                            justifyContent: isEditingAmbition ? 'flex-start' : 'center',
                            alignItems: 'stretch',
                        }}
                        onClick={() => {
                            if (!isEditingAmbition && canEdit) setIsEditingAmbition(true)
                        }}
                    >
                        {isEditingAmbition ? (
                            <InputBase
                                value={ambitionDraft}
                                onChange={(e) => setAmbitionDraft(e.target.value)}
                                autoFocus
                                fullWidth
                                multiline
                                minRows={2}
                                placeholder="Add your asset ambition / vision..."
                                onBlur={handleSaveAmbition}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        e.preventDefault()
                                        handleCancelAmbition()
                                    }
                                    if ((e.key === 'Enter' && (e.metaKey || e.ctrlKey)) || e.key === 'Tab') {
                                        // save on Cmd/Ctrl+Enter or Tab
                                        e.preventDefault()
                                        handleSaveAmbition()
                                    }
                                }}
                                inputRef={ambitionInputRef}
                                sx={(theme) => ({
                                    p: 0,
                                    m: 0,
                                    ...(theme.typography as any)['16x400'],
                                    color: 'figma.grayscale.textHier1',
                                    '& .MuiInputBase-input': {
                                        ...(theme.typography as any)['16x400'],
                                        color: 'figma.grayscale.textHier1',
                                        p: 0,
                                        m: 0,
                                        lineHeight: (theme.typography as any)['16x400']?.lineHeight,
                                    },
                                    '& .MuiInputBase-inputMultiline': {
                                        ...(theme.typography as any)['16x400'],
                                        color: 'figma.grayscale.textHier1',
                                        p: 0,
                                        m: 0,
                                        lineHeight: (theme.typography as any)['16x400']?.lineHeight,
                                    },
                                    '& textarea': {
                                        ...(theme.typography as any)['16x400'],
                                        color: 'figma.grayscale.textHier1',
                                        lineHeight: (theme.typography as any)['16x400']?.lineHeight,
                                        resize: 'none',
                                        overflow: 'auto',
                                        padding: 0,
                                        margin: 0,
                                    },
                                })}
                            />
                        ) : (
                            <Box sx={{ width: '100%' }}>
                                <Typography
                                    variant="16x400"
                                    color="figma.grayscale.textHier1"
                                    sx={{ whiteSpace: 'pre-line', m: 0 }}
                                >
                                    {assetAmbition || 'Click to add your asset ambition / vision...'}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Stack>
                {tppId && <TppAdminSettingsButton tppId={tppId} sections={sections} rows={rows} canEdit={canEdit} />}
            </Stack>
        </Stack>
    )
}

export default TppHeader
